/**
 * Phonetic / multilingual parse of coach table-setup voice commands.
 * Seating names resolve against present players; add-player against group roster.
 */

import { normalizeTranscription } from "../voice/TranscriptionNormalizer";

const COMMAND_PHRASES = {
  rotate_left: [
    "rotate left",
    "rotate to the left",
    "rotation left",
    "tourner a gauche",
    "tourner à gauche",
    "pivoter a gauche",
    "pivoter à gauche",
    "rotation gauche",
    "nach links rotieren",
    "links rotieren",
    "rotar a la izquierda",
    "rotar izquierda",
    "naar links draaien",
    "links doorschuiven",
    "rodar para a esquerda",
    "rodar a esquerda",
  ],
  rotate_right: [
    "rotate right",
    "rotate to the right",
    "rotation right",
    "tourner a droite",
    "tourner à droite",
    "pivoter a droite",
    "pivoter à droite",
    "rotation droite",
    "nach rechts rotieren",
    "rechts rotieren",
    "rotar a la derecha",
    "rotar derecha",
    "naar rechts draaien",
    "rechts doorschuiven",
    "rodar para a direita",
    "rodar a direita",
  ],
  sort_ranking_desc: [
    "sort by ranking",
    "sort by rating",
    "sort by points",
    "order by ranking",
    "assign by ranking",
    "ranking top to bottom",
    "rating top to bottom",
    "best players first",
    "trier par classement",
    "trier par points",
    "classer par ranking",
    "classement du haut vers le bas",
    "meilleurs joueurs d abord",
    "meilleurs joueurs d'abord",
    "nach ranking sortieren",
    "nach punkten sortieren",
    "ordenar por ranking",
    "ordenar por puntos",
    "sorteren op ranking",
    "ordenar por ranking",
    "ordenar por pontos",
  ],
  sort_ranking_asc: [
    "sort ranking bottom to top",
    "sort by ranking ascending",
    "sort by ranking bottom to top",
    "ranking bottom to top",
    "rating bottom to top",
    "lowest ranking first",
    "worst players first",
    "trier classement bas vers haut",
    "classement du bas vers le haut",
    "moindres points d abord",
    "moindres points d'abord",
    "ranking aufsteigend",
    "niedrigste punkte zuerst",
    "ranking de abajo hacia arriba",
    "ranking oplopend",
    "ranking de baixo para cima",
  ],
  switch_players: [
    "switch players",
    "swap players",
    "switch",
    "swap",
    "échanger",
    "echanger",
    "inverser",
    "permuter",
    "wechseln",
    "tauschen",
    "intercambiar",
    "cambiar",
    "wisselen",
    "omwisselen",
    "trocar",
  ],
  add_player: [
    "add player",
    "add players",
    "mark present",
    "mark as present",
    "set present",
    "check in",
    "ajouter joueur",
    "ajouter un joueur",
    "ajouter des joueurs",
    "ajouter",
    "ajoute",
    "marquer present",
    "marquer présent",
    "mettre present",
    "mettre présent",
    "presence",
    "présence",
    "spieler hinzufugen",
    "spieler hinzufügen",
    "als anwesend",
    "anwesend",
    "anwesenheit",
    "anadir jugador",
    "añadir jugador",
    "añadir",
    "anadir",
    "marcar presente",
    "presencia",
    "speler toevoegen",
    "toevoegen",
    "aanwezig",
    "adicionar jogador",
    "adicionar",
    "presenca",
    "presença",
    "add",
  ],
  move_player: [
    "move",
    "send",
    "put",
    "place",
    "seat",
    "assign",
    "to table",
    "on table",
    "at table",
    "table",
    "deplacer",
    "déplacer",
    "envoyer",
    "mettre",
    "placer",
    "a table",
    "à table",
    "va a table",
    "va à table",
    "vers table",
    "verschieben",
    "schicken",
    "an tisch",
    "zu tisch",
    "tisch",
    "mover",
    "enviar",
    "a mesa",
    "mesa",
    "verplaats",
    "naar tafel",
    "tafel",
    "mover",
    "enviar",
    "para mesa",
    "mesa",
  ],
  create_station: [
    "create station",
    "create a station",
    "new station",
    "add station",
    "split station",
    "divide the group",
    "divide group",
    "make a station",
    "creer une station",
    "créer une station",
    "nouvelle station",
    "ajouter une station",
    "diviser le groupe",
    "creer un sous groupe",
    "créer un sous groupe",
    "station erstellen",
    "neue station",
    "gruppe teilen",
    "crear estacion",
    "crear estación",
    "nueva estacion",
    "nueva estación",
    "station aanmaken",
    "nieuwe station",
    "groep splitsen",
    "criar estacao",
    "criar estação",
    "nova estacao",
    "nova estação",
  ],
};

const TABLE_NUMBER_RE =
  /(?:table|tisch|mesa|tafel|tabla)\s*(?:number|nummer|numero|número|n[°º.]?)?\s*(\d{1,2})\b|\b(?:t|n[°º.]?)\s*(\d{1,2})\b|\b(\d{1,2})\b/i;

/** Spoken cardinals → digits (EN/FR/DE/ES/NL/PT). Longer phrases first. */
const SPOKEN_NUMBER_ENTRIES = [
  ["twenty one", 21],
  ["vingt et un", 21],
  ["twenty", 20],
  ["vingt", 20],
  ["nineteen", 19],
  ["dix neuf", 19],
  ["dix-neuf", 19],
  ["eighteen", 18],
  ["dix huit", 18],
  ["dix-huit", 18],
  ["seventeen", 17],
  ["dix sept", 17],
  ["dix-sept", 17],
  ["sixteen", 16],
  ["seize", 16],
  ["fifteen", 15],
  ["quinze", 15],
  ["fourteen", 14],
  ["quatorze", 14],
  ["thirteen", 13],
  ["treize", 13],
  ["twelve", 12],
  ["douze", 12],
  ["zwolf", 12],
  ["doce", 12],
  ["twaalf", 12],
  ["eleven", 11],
  ["onze", 11],
  ["elf", 11],
  ["once", 11],
  ["ten", 10],
  ["dix", 10],
  ["zehn", 10],
  ["diez", 10],
  ["tien", 10],
  ["dez", 10],
  ["nine", 9],
  ["neuf", 9],
  ["neun", 9],
  ["nueve", 9],
  ["negen", 9],
  ["nove", 9],
  ["eight", 8],
  ["huit", 8],
  ["acht", 8],
  ["ocho", 8],
  ["oito", 8],
  ["seven", 7],
  ["sept", 7],
  ["sieben", 7],
  ["siete", 7],
  ["zeven", 7],
  ["sete", 7],
  ["six", 6],
  ["seis", 6],
  ["sechs", 6],
  ["zes", 6],
  ["five", 5],
  ["cinq", 5],
  ["funf", 5],
  ["cinco", 5],
  ["vijf", 5],
  ["four", 4],
  ["quatre", 4],
  ["vier", 4],
  ["cuatro", 4],
  ["quatro", 4],
  ["three", 3],
  ["trois", 3],
  ["drei", 3],
  ["tres", 3],
  ["três", 3],
  ["drie", 3],
  ["two", 2],
  ["deux", 2],
  ["zwei", 2],
  ["dos", 2],
  ["dois", 2],
  ["twee", 2],
  ["one", 1],
  ["un", 1],
  ["une", 1],
  ["eins", 1],
  ["uno", 1],
  ["uma", 1],
  ["een", 1],
].sort((a, b) => b[0].length - a[0].length);

function fold(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Replace spoken number words with digits so table parsing accepts "table two" / "table quatre". */
export function expandSpokenNumbers(text) {
  let out = fold(text);
  if (!out) return "";
  for (const [word, n] of SPOKEN_NUMBER_ENTRIES) {
    const foldedWord = fold(word);
    if (!foldedWord) continue;
    const re = new RegExp(`\\b${foldedWord.replace(/\s+/g, "\\s+")}\\b`, "g");
    out = out.replace(re, String(n));
  }
  return out.replace(/\s+/g, " ").trim();
}

function phraseMatches(haystack, phrase) {
  const p = fold(phrase);
  if (!p) return false;
  return ` ${haystack} `.includes(` ${p} `) || haystack.includes(p);
}

function findBestCommand(normalized) {
  const hay = fold(normalized);
  let best = null;
  for (const [action, phrases] of Object.entries(COMMAND_PHRASES)) {
    for (const phrase of phrases) {
      if (!phraseMatches(hay, phrase)) continue;
      const score = fold(phrase).length;
      if (!best || score > best.score) {
        best = { action, score, phrase };
      }
    }
  }
  return best;
}

export function extractTableNumber(text) {
  const expanded = expandSpokenNumbers(text);
  if (!expanded) return null;
  // Bare reply after "say a table number": "two", "quatre", "3"
  if (/^\d{1,2}$/.test(expanded)) {
    const alone = Number(expanded);
    return Number.isFinite(alone) && alone > 0 ? alone : null;
  }
  const m = expanded.match(TABLE_NUMBER_RE);
  if (!m) return null;
  const n = Number(m[1] || m[2] || m[3]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Score how well a spoken fragment matches a player name.
 * Matches first name, last name, and nickname/alias.
 */
function scorePlayerMatch(spokenFold, player) {
  const first = fold(player.user_first_name || player.firstName || "");
  const last = fold(player.user_last_name || player.lastName || "");
  const alias = fold(player.user_alias || player.alias || "");
  const display = fold(player.name || "");
  const full = fold(
    [first, last].filter(Boolean).join(" ") || display || alias
  );
  if (!spokenFold || !(full || alias || first || last)) return 0;

  let score = 0;
  if (spokenFold.includes(full) && full.length >= 3) score += 100 + full.length;
  if (first && first.length >= 2 && spokenFold.includes(first))
    score += 40 + first.length;
  if (last && last.length >= 2 && spokenFold.includes(last))
    score += 50 + last.length;
  // Nicknames / aliases: allow short exact nicknames (2+)
  if (alias && alias.length >= 2 && spokenFold.includes(alias))
    score += 55 + alias.length;
  if (display && display.length >= 3 && spokenFold.includes(display))
    score += 30 + Math.min(display.length, 12);

  if (first && first.length >= 3) {
    const stub = first.slice(0, 3);
    if (spokenFold.includes(stub)) score += 12;
  }
  return score;
}

function matchPlayersBySpokenName(spoken, players = [], { max = 2 } = {}) {
  const hay = fold(spoken);
  const scored = (players || [])
    .map((p) => ({
      player: p,
      score: scorePlayerMatch(hay, p),
      id: String(p.player_id || p.playerId || p.id || ""),
    }))
    .filter((x) => x.score >= 40 && x.id)
    .sort((a, b) => b.score - a.score);

  const out = [];
  const seen = new Set();
  for (const row of scored) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    out.push(row.player);
    if (out.length >= max) break;
  }
  return out;
}

function matchPresentPlayers(spoken, presentPlayers = [], { max = 2 } = {}) {
  return matchPlayersBySpokenName(spoken, presentPlayers, { max });
}

export function isSessionPresentPlayer(player) {
  const v = player?.presensee;
  return v === true || v === 1 || v === "1" || v === "true";
}

function matchGroupPlayersToAdd(spoken, groupRoster = [], { max = 6 } = {}) {
  const candidates = (groupRoster || []).filter(
    (p) => !isSessionPresentPlayer(p)
  );
  return matchPlayersBySpokenName(spoken, candidates, { max });
}

function playerFirstName(player) {
  return fold(player?.user_first_name || player?.firstName || "");
}

function playerLastName(player) {
  return fold(player?.user_last_name || player?.lastName || "");
}

/**
 * Two or more group members share the spoken first name, and no last name
 * was spoken. Other clubs are not part of this decision.
 */
function sharedUnqualifiedFirstName(spokenFold, players = []) {
  if (!players || players.length < 2) return false;
  const firsts = players.map(playerFirstName);
  if (firsts.some((name) => name.length < 2)) return false;
  const shared = firsts[0];
  if (!firsts.every((name) => name === shared)) return false;
  if (!spokenFold.includes(shared)) return false;
  const lasts = players.map(playerLastName);
  if (lasts.some((last) => last.length >= 2 && spokenFold.includes(last))) {
    return false;
  }
  return true;
}

/**
 * One confident roster match, or none when the name is missing or ambiguous.
 * Used by Expert Coach capture so a note is not attached to the wrong player.
 */
export function identifySpokenPlayer(spoken, players = []) {
  const hay = fold(spoken);
  const scored = (players || [])
    .map((p) => ({
      player: p,
      score: scorePlayerMatch(hay, p),
      id: String(p.player_id || p.playerId || p.id || ""),
    }))
    .filter((x) => x.score >= 40 && x.id)
    .sort((a, b) => b.score - a.score);
  if (!scored.length) return { player: null, confident: false };
  if (scored.length > 1 && scored[0].score - scored[1].score < 15) {
    return { player: null, confident: false, ambiguous: true };
  }
  return { player: scored[0].player, confident: true, score: scored[0].score };
}

export function playerVoiceLabel(player) {
  if (!player) return "Player";
  const alias = String(player.user_alias || player.alias || "").trim();
  if (alias) return alias;
  const first = String(player.user_first_name || player.firstName || "").trim();
  const last = String(player.user_last_name || player.lastName || "").trim();
  return (
    [first, last].filter(Boolean).join(" ") ||
    String(player.name || "").trim() ||
    "Player"
  );
}

/**
 * @param {string} rawTranscript
 * @param {{
 *   presentPlayers?: Array<object>,
 *   groupRoster?: Array<object>,
 *   language?: string|null
 * }} [opts]
 */
export function parseTableSetupVoiceCommand(rawTranscript, opts = {}) {
  const raw = String(rawTranscript || "").trim();
  if (!raw) return null;
  const normalized = normalizeTranscription(raw);
  const present = Array.isArray(opts.presentPlayers) ? opts.presentPlayers : [];
  const groupRoster = Array.isArray(opts.groupRoster)
    ? opts.groupRoster
    : present;
  const pendingMovePlayers = Array.isArray(opts.pendingMovePlayers)
    ? opts.pendingMovePlayers
    : [];

  // Follow-up after "need_table": coach says only "two" / "table quatre" / "3"
  if (pendingMovePlayers.length) {
    const followUpTable = extractTableNumber(normalized);
    const followCmd = findBestCommand(normalized);
    const isTableOnlyReply =
      followUpTable &&
      (!followCmd ||
        followCmd.action === "move_player" ||
        /^\d{1,2}$/.test(expandSpokenNumbers(normalized)) ||
        /^(table|tisch|mesa|tafel|tabla)\s+\d{1,2}$/i.test(
          expandSpokenNumbers(normalized)
        ));
    if (isTableOnlyReply) {
      return {
        action: "move_player",
        tableNumber: followUpTable,
        players: pendingMovePlayers,
        raw,
        normalized,
        force: true,
      };
    }
  }

  const cmd = findBestCommand(normalized);
  if (!cmd) return null;

  if (cmd.action === "rotate_left" || cmd.action === "rotate_right") {
    return { action: cmd.action, raw, normalized };
  }

  if (cmd.action === "sort_ranking_desc" || cmd.action === "sort_ranking_asc") {
    return { action: cmd.action, raw, normalized };
  }

  if (cmd.action === "switch_players") {
    const players = matchPresentPlayers(normalized, present, { max: 2 });
    if (players.length < 2) {
      return {
        action: cmd.action,
        players,
        raw,
        normalized,
        incomplete: true,
        reason: "need_two_players",
      };
    }
    return { action: cmd.action, players, raw, normalized };
  }

  if (cmd.action === "add_player") {
    const players = matchGroupPlayersToAdd(normalized, groupRoster, {
      max: 8,
    });
    if (
      sharedUnqualifiedFirstName(fold(normalized), players)
    ) {
      return {
        action: cmd.action,
        players,
        raw,
        normalized,
        incomplete: true,
        reason: "ambiguous_group_player",
      };
    }
    if (!players.length) {
      const already = matchPresentPlayers(normalized, present, { max: 4 });
      if (already.length) {
        return {
          action: cmd.action,
          players: already,
          raw,
          normalized,
          incomplete: true,
          reason: "already_present",
        };
      }
      return {
        action: cmd.action,
        players,
        raw,
        normalized,
        incomplete: true,
        reason: "need_group_player",
      };
    }
    const spokenFold = fold(normalized);
    const byLastName = players.filter((player) => {
      const last = playerLastName(player);
      return last.length >= 2 && spokenFold.includes(last);
    });
    return {
      action: cmd.action,
      players: byLastName.length ? byLastName : players,
      raw,
      normalized,
    };
  }

  if (cmd.action === "move_player") {
    const tableNumber = extractTableNumber(normalized);
    const players = matchPresentPlayers(normalized, present, { max: 8 });
    if (!tableNumber || !players.length) {
      return {
        action: cmd.action,
        tableNumber,
        players,
        raw,
        normalized,
        incomplete: true,
        reason: !tableNumber ? "need_table" : "need_player",
      };
    }
    return {
      action: cmd.action,
      tableNumber,
      players,
      raw,
      normalized,
      force: true,
    };
  }

  if (cmd.action === "create_station") {
    const tableNumber = extractTableNumber(normalized);
    const players = matchPresentPlayers(normalized, present, { max: 12 });
    return {
      action: cmd.action,
      tableNumber,
      players,
      raw,
      normalized,
      deferToAi: true,
    };
  }

  return null;
}

export function presentPlayerVoiceHints(presentPlayers = []) {
  return (presentPlayers || [])
    .map((p) => {
      const first = String(p.user_first_name || p.firstName || "").trim();
      const last = String(p.user_last_name || p.lastName || "").trim();
      const alias = String(p.user_alias || p.alias || "").trim();
      const name =
        [first, last].filter(Boolean).join(" ") ||
        alias ||
        String(p.name || "").trim();
      if (!name) return null;
      return alias && alias !== name ? `${name} (${alias})` : name;
    })
    .filter(Boolean)
    .slice(0, 40);
}

/** Group members not yet marked present — for add-player voice / AI context. */
export function groupRosterNotPresentHints(groupRoster = []) {
  return presentPlayerVoiceHints(
    (groupRoster || []).filter((p) => !isSessionPresentPlayer(p))
  );
}

export { COMMAND_PHRASES, fold as foldTableSetupVoiceText };
